import {
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import Button from "components/form/Button";
import { SettingsValue } from "types/payments";
import { CashAppMobileIcon } from "icons/payments/CashAppMobileIcon";
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { useUserData } from "store/slices/authSlice";
import {
  onSetFinalDisclosureLoading,
  onSetShowDisclosure,
  updateCheckAmountState,
  useShowFinalDisclosure,
} from "store/slices/commonSlice";
import { usePortalSettingsData } from "store/slices/dashboardSlice";
import {
  duplicatePaymentGet,
  resetCheckDuplicatePayment,
  resetDuplicateOrder,
  storePaymnetPlan,
  setIsPaymentProcessing,
} from "store/slices/paymentsSlice";
import {
  checkOverPaymentAmount,
  checkPhone,
  getCountryShortName,
  validateEmail,
} from "utils/HelperFunctions";
import { PhoneInput } from "types/common";
import { getCountryCallingCode } from "react-phone-number-input";
import { CountryCode } from "types/UserProfile";
import { RootState } from "store/store";
import MobileInputNew from "components/form/MobileInputNew";
import { ConvenienceFeeDisplay } from "./PaymentMethodComponents";
import { buildAccountBillingDetails } from "utils/stripeBillingDetails";

interface CashAppFormProps {
  isRecurring?: boolean;
  isLoading?: boolean;
  isDisabled?: boolean;
  isRetry?: boolean;
  hasFuture?: boolean;
  isSchedulePayment?: boolean;
  isProductLink?: boolean;
  isAgreement?: boolean;
  isPaymentLink?: boolean;
  paymentPlan?: any | null;
  buttonText?: string;
  cashAppURL: string;
  email?: string;
  setEmail?: (val: string) => void;
  handleBack?: () => void;
  setShowPreview?: (val: boolean) => void;
  handleClick?: () => void;
  isUpdateSubscription?: boolean;
  isUpdatePlan?: boolean;
  amount?: any;
  checkNegativeBalance?: boolean;
  accounts_id?: string | null;
  collectPhone?: boolean;
  phoneNumber?: PhoneInput;
  setPhoneNumber?: React.Dispatch<React.SetStateAction<PhoneInput>>;
  isUpdateSubscriptionPaymentMethod?: boolean;
  showConvFee?: boolean;
  isDowngradePlan?: boolean;
  handleEmailBlur?: () => void;
  orderId?: number;
  onIntentFailure?: (orderId: number, error: any) => Promise<void>;
}
let globalCashAppStripeInstance: any = null;
let globalCashAppElements: any = null;

const CashAppForm = forwardRef(
  (
    {
      isRecurring = false,
      isLoading,
      paymentPlan = null,
      isDisabled,
      cashAppURL,
      isRetry = false,
      hasFuture = false,
      buttonText = "",
      isSchedulePayment = false,
      isAgreement,
      handleBack = () => {},
      isProductLink = false,
      email = "",
      setEmail = () => {},
      isPaymentLink = true,
      setShowPreview = () => {},
      handleClick = () => {},
      isUpdateSubscription = false,
      isUpdatePlan = false,
      amount = 0,
      checkNegativeBalance = false,
      accounts_id = "",
      collectPhone = false,
      phoneNumber = { country_code: "", phone: "", postal_code: "" },
      setPhoneNumber = () => {},
      isUpdateSubscriptionPaymentMethod = false,
      showConvFee = false,
      isDowngradePlan = false,
      handleEmailBlur = () => {},
      orderId = 0,
      onIntentFailure = async () => {},
    }: CashAppFormProps,
    ref
  ) => {
    const stripe = useStripe();
    const elements = useElements();
    const dispatch = useAppDispatch();
    const portalSettings = usePortalSettingsData();
    const showFinalDisclosure = useShowFinalDisclosure();
    const { conFee, isFeeLoading, checkedDuplicatePayment } = useAppSelector(
      (state) => state.payment
    );

    const { isChecking, isSubscriptionAllowed } = useAppSelector(
      (state: RootState) => state.subscription
    );
    const { checkAmount } = useAppSelector((state: RootState) => state.common);
    const [isRevealLoading, setIsRevealLoading] = useState<boolean>(false);
    const userData = useUserData();

    const handlePayment = async () => {
      setIsRevealLoading(true);
      if (!stripe || !elements) {
        dispatch(setIsPaymentProcessing(false));
        return;
      }
      if (paymentPlan) {
        await dispatch(storePaymnetPlan(paymentPlan));
      }

      const billingDetails = buildAccountBillingDetails({
        account: userData?.data?.accountData,
        // the email on the page (prefilled from the account) first
        email: email || userData?.data?.accountData?.email,
        phone: collectPhone ? phoneNumber?.phone : undefined,
      });
      // Reset payment processing after redirect is initiated
      dispatch(setIsPaymentProcessing(false));
      const activeStripe = showFinalDisclosure && globalCashAppStripeInstance ? globalCashAppStripeInstance : stripe;
      const activeElements = showFinalDisclosure && globalCashAppElements ? globalCashAppElements : elements;

      const { error } = await activeStripe.confirmPayment({
        elements: activeElements,
        confirmParams: {
          return_url: cashAppURL,
          payment_method_data: {
            billing_details: billingDetails,
          },
        },
      });

      if (error) {
        await onIntentFailure(orderId, error);
        console.log(error.message);
      }
    };

    useEffect(() => {
      if (userData?.data?.accountData?.email) {
        setEmail(userData?.data?.accountData?.email || "");
      }
    }, [userData]);

    const handleSetup = async () => {
      if (!stripe || !elements) {
        dispatch(setIsPaymentProcessing(false));
        return;
      }

      if (paymentPlan) {
        await dispatch(storePaymnetPlan(paymentPlan));
      }

      const billingDetails = buildAccountBillingDetails({
        account: userData?.data?.accountData,
        // the email on the page (prefilled from the account) first
        email: email || userData?.data?.accountData?.email,
        phone: collectPhone ? phoneNumber?.phone : undefined,
      });
      // Reset payment processing after redirect is initiated
      dispatch(setIsPaymentProcessing(false));
      const activeStripe = showFinalDisclosure && globalCashAppStripeInstance ? globalCashAppStripeInstance : stripe;
      const activeElements = showFinalDisclosure && globalCashAppElements ? globalCashAppElements : elements;

      const { error } = await activeStripe.confirmSetup({
        elements: activeElements,
        confirmParams: {
          return_url: cashAppURL,
          payment_method_data: {
            billing_details: billingDetails,
          },
        },
      });

      // Reset payment processing after redirect is initiated
      dispatch(setIsPaymentProcessing(false));

      if (error) {
        await onIntentFailure(orderId, error);
        console.log(error.message);
      }
    };

    const checkPastAmount = async () => {
      try {
        let payload = {
          accounts_id: userData?.data?.accountData?.id ?? accounts_id,
          payment_method: "CAPP",
          amount: amount,
        };
        let res: any = await dispatch(duplicatePaymentGet(payload)).unwrap();
        const { isDuplicateOrders, duplicateOrdersCount } = res?.body?.data;
        if (isDuplicateOrders && duplicateOrdersCount > 0) {
          return false;
        } else {
          return true;
        }
      } catch (error) {
        console.log("error =====", error);
      }
    };

    const handleCheckDisclosureAndDuplicate = async () => {
      try {
        let moveAhead =
          !checkedDuplicatePayment && !isProductLink
            ? await checkPastAmount()
            : true;
        handleClick();
        if (!moveAhead) {
          setIsRevealLoading(false);
          dispatch(onSetFinalDisclosureLoading(false));
          return false;
        } else {
          if (
            (portalSettings?.payment_agreement_opt === SettingsValue.CUSTOM ||
              portalSettings?.payment_agreement_opt ===
                SettingsValue.DEFAULT) &&
            !showFinalDisclosure
          ) {
            setIsRevealLoading(false);
            dispatch(onSetFinalDisclosureLoading(false));
            dispatch(resetDuplicateOrder());
            globalCashAppStripeInstance = stripe;
            globalCashAppElements = elements;
            dispatch(onSetShowDisclosure(true));
          } else {
            dispatch(resetDuplicateOrder());
            dispatch(resetCheckDuplicatePayment());
            return true;
          }
        }
      } catch (error) {
        console.log("error =====", error);
      }
    };

    const handlePhoneCountryChange = useCallback((itm: string) => {
      setPhoneNumber((state) => ({
        ...state,
        country_code: getCountryCallingCode(itm as CountryCode),
      }));
    }, []);

    const handleOnPhoneChange = useCallback((phone: string) => {
      // if (phone?.length > 3) {
      // const isCheckValid = isValidPhoneNumber(phone);
      //   setIsValidNumber(isCheckValid);
      // } else {
      //   setIsValidNumber(true);
      // }
      setPhoneNumber((state) => ({
        ...state,
        phone,
      }));
    }, []);

    const handleSubmit = async () => {
      setIsRevealLoading(true);
      dispatch(setIsPaymentProcessing(true));
      if (paymentPlan) {
        await dispatch(storePaymnetPlan(paymentPlan));
      }

      if (!isUpdateSubscriptionPaymentMethod) {
        let negativeBalance =
          !checkAmount && checkNegativeBalance
            ? await checkOverPaymentAmount({
                amount: Number(amount),
                balance: userData?.data?.accountData?.balance,
                overpayment_protection: portalSettings?.overpayment_protection,
                dispatch,
              })
            : true;
        if (!negativeBalance) {
          setIsRevealLoading(false);
          dispatch(setIsPaymentProcessing(false));
          return;
        }
        let moveAhead = await handleCheckDisclosureAndDuplicate();
        if (!moveAhead) {
          return;
        }
        dispatch(updateCheckAmountState(false));
      }
      try {
        if (!stripe || !elements) {
          dispatch(setIsPaymentProcessing(false));
          return;
        }
        if (isRecurring) {
          await handleSetup();
        } else {
          await handlePayment();
        }
      } catch (error) {
        console.log("handleSubmit error =====", error);
      } finally {
        dispatch(onSetFinalDisclosureLoading(false));
        setIsRevealLoading(false);
        dispatch(setIsPaymentProcessing(false));
      }
    };

    useImperativeHandle(ref, () => ({
      callHandleClick: () => handleSubmit(),
    }));

    const amountCheck = useMemo(() => {
      if (isDowngradePlan || isUpdateSubscriptionPaymentMethod) {
        return true;
      }
      return parseFloat(`${amount}`) < 1;
    }, [amount, isDowngradePlan, isUpdateSubscriptionPaymentMethod]);

    if (isRetry && hasFuture) {
      return (
        <>
          <div className="none hidden">
            <PaymentElement />
          </div>
          <Button
            variant="variable"
            text={buttonText}
            className="w-full"
            isLoading={isRevealLoading}
            isDisabled={isDisabled}
            onClick={() => {
              handleSubmit();
            }}
          />
        </>
      );
    }

    if (isRetry && !hasFuture) {
      return (
        <>
          <div className="none hidden">
            <PaymentElement />
          </div>
          <Button
            variant="variable"
            text={buttonText}
            className="max-w-40 w-full"
            isLoading={isRevealLoading}
            isDisabled={isDisabled}
            onClick={() => {
              handleSubmit();
            }}
          />
        </>
      );
    }

    return (
      <div>
        <div className="none hidden">
          <PaymentElement />
        </div>

        <div>
          <div className="flex gap-4 py-3 px-6 border-[1px] border-strokePrimary rounded">
            <div>
              <CashAppMobileIcon />
            </div>
            <p className="lg:text-base text-sm text-txtSecondary">
              After submitting your order, scan the QR code using Cash App Pay.
            </p>
          </div>
          {showConvFee &&
            portalSettings?.convenience_fee_opt !== SettingsValue.NO && (
              <ConvenienceFeeDisplay
                conFee={conFee}
                isFeeLoading={isFeeLoading}
                isPaymentLink={isPaymentLink}
                setShowPreview={setShowPreview}
                customClassName="mt-2 mb-5"
              />
            )}
        </div>

        {isProductLink && (
          <div className="py-4">
            <div className="text-xs leading-[15.62px] font-bold text-txtSecondary mb-3 -mt-3">
              EMAIL
            </div>
            <div className="relative w-full">
              <input
                autoComplete="off"
                className={`w-full border-strokePrimary hover:!border-strokeActive rounded-[5px] border py-2 px-[15px] bg-bgPrimary text-sm text-txtPrimary font-normal leading-normal placeholder:text-txtSecondary focus:outline-none ${
                  userData?.data?.accountData?.email &&
                  "!cursor-not-allowed	!bg-bgSecondary"
                }`}
                placeholder="Email address"
                onChange={(e) => {
                  setEmail(
                    userData?.data?.accountData?.email
                      ? userData?.data?.accountData?.email
                      : e.target.value
                  );
                }}
                onBlur={handleEmailBlur}
                value={
                  userData?.data?.accountData?.email
                    ? userData?.data?.accountData?.email
                    : email
                }
                disabled={userData?.data?.accountData?.email}
              />
            </div>

            {collectPhone && (
              <div className="pt-5">
                <div
                  className={`!text-xs !font-bold !leading-normal text-txtSecondary uppercase `}
                >
                  Phone
                </div>
                <MobileInputNew
                  inputValue={phoneNumber?.phone || ""}
                  isProductLinkInput={true}
                  userCountry={
                    getCountryShortName(phoneNumber?.country_code) || "US"
                  }
                  setCountry={handlePhoneCountryChange}
                  setInputValue={handleOnPhoneChange}
                />
              </div>
            )}
          </div>
        )}

        {isSchedulePayment ? (
          <div
            className="lg:absolute bg-white lg:bottom-0 left-0 py-4 z-20 border-t w-full gap-5"
          >
            <div
              className={`w-full px-5 ${isUpdatePlan ? "lg:px-10 flex gap-7" : "lg:px-8"}`}
            >
              {isUpdatePlan && (
                <div className="lg:w-[5.2%] md2:w-[5.5%] w-0"></div>
              )}

              <div className="w-full flex justify-between gap-5">
                <Button
                  className="lg:!w-40 !w-full"
                  isDisabled={isLoading || isRevealLoading}
                  text="Back"
                  type="button"
                  variant="secondary"
                  onClick={handleBack}
                />
                <Button
                  className="lg:!w-48 !w-full"
                  isLoading={isLoading || isRevealLoading}
                  isDisabled={isDisabled}
                  text={isUpdatePlan ? "Update" : "Reveal QR code"}
                  type="button"
                  onClick={() => {
                    handleSubmit();
                  }}
                  variant="variable"
                />
              </div>
            </div>
          </div>
        ) : isAgreement ? (
          <div className="w-full flex lg:absolute bg-white pt-4 xl:pr-[3rem] lg:pl-[5rem] xl:pl-[7.5rem] lg:px-5 lg:-bottom-4 -bottom-48 left-0 justify-between items-center">
            <Button
              text={`Back`}
              variant="white"
              onClick={handleBack}
              className="lg:w-40 w-full max-w-40"
            />
            <Button
              text={`Confirm`}
              isDisabled={
                isDisabled ||
                (isProductLink && !validateEmail(email)) ||
                (collectPhone && !checkPhone(phoneNumber))
              }
              isLoading={isLoading || isRevealLoading}
              onClick={() => {
                handleSubmit();
              }}
              className="lg:w-40 w-full max-w-40"
              variant="variable"
            />
          </div>
        ) : (
          <Button
            text={
              isUpdateSubscription || isUpdateSubscriptionPaymentMethod
                ? "Update"
                : "Reveal QR code"
            }
            className="!w-full"
            isDisabled={
              isDisabled ||
              (isProductLink &&
                (!validateEmail(email) || !isSubscriptionAllowed)) ||
              (collectPhone && !checkPhone(phoneNumber))
            }
            onClick={() => {
              handleSubmit();
            }}
            isLoading={
              isLoading || isRevealLoading || isFeeLoading || isChecking
            }
            variant="variable"
          />
        )}
      </div>
    );
  }
);

export default CashAppForm;
